import { DragEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StepActions } from '../components/StepActions';
import { Alert } from '../components/Alert';
import { uploadDocument, RegistrationApiError } from '../../../../services/registrationApi';
import { ACCEPTED_DOC_TYPES, formatFileSize, validateDocumentFile } from '../utils';

interface DocumentStepProps {
  registrationId: string;
  onComplete: () => void;
  onBack: () => void;
}

/**
 * Passo condicional: envio do documento comprobatório de titularidade.
 * Validação de tipo/tamanho no cliente; verificação real (LLM) é assíncrona no backend.
 */
export function DocumentStep({ registrationId, onComplete, onBack }: DocumentStepProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Libera o object URL do preview ao trocar/desmontar (evita vazamento de memória).
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const selectFile = (selected: File | undefined) => {
    if (!selected) return;
    const validation = validateDocumentFile(selected);
    if (!validation.ok) {
      setError(validation.error || t('views.registration.document.invalid_file', 'Arquivo inválido.'));
      return;
    }
    setError('');
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(selected.type.startsWith('image/') ? URL.createObjectURL(selected) : null);
    setFile(selected);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    selectFile(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!file) {
      setError(t('views.registration.document.select_error', 'Selecione um documento para enviar.'));
      return;
    }
    setSubmitting(true);
    try {
      await uploadDocument({ registrationId, file });
      onComplete();
    } catch (err) {
      setError(
        err instanceof RegistrationApiError
          ? err.message
          : t('views.registration.document.upload_error', 'Não foi possível enviar o documento. Tente novamente.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="mb-[16px] p-[12px] rounded-[8px] bg-sky-50 text-[12px] text-navy-800 leading-[1.5] whitespace-pre-line">
        <i className="fa fa-info-circle text-navy-700 mr-2" aria-hidden="true"></i>
        {t('views.registration.document.info', 'Envie um documento que comprove sua titularidade (ex.: conta de consumo, contrato ou escritura). A análise é automática e pode exigir aprovação da administração.')}
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {!file ? (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={t('views.registration.document.select_aria', 'Selecionar documento')}
          className={`flex flex-col items-center justify-center gap-2 py-[32px] px-[16px] rounded-[12px] border-2 border-dashed cursor-pointer transition-colors ${
            dragging ? 'border-sky-500 bg-sky-50' : 'border-mist-300 bg-mist-100 hover:border-sky-500'
          }`}
        >
          <i className="fa fa-cloud-upload text-[28px] text-sky-500" aria-hidden="true"></i>
          <p className="text-[13px] font-semibold text-navy-800">
            {t('views.registration.document.drop_hint', 'Arraste o arquivo aqui ou clique para selecionar')}
          </p>
          <p className="text-[11px] text-navy-700/70">{t('views.registration.document.file_types', 'JPG, PNG, WEBP ou PDF · até 8 MB')}</p>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-[12px] rounded-[12px] border border-solid border-mist-300 bg-white">
          {previewUrl ? (
            <img src={previewUrl} alt={t('views.registration.document.preview_alt', 'Pré-visualização do documento')} className="w-[54px] h-[54px] object-cover rounded-[8px]" />
          ) : (
            <div className="w-[54px] h-[54px] rounded-[8px] bg-mist-100 flex items-center justify-center">
              <i className="fa fa-file-pdf-o text-[24px] text-[#e84b3d]" aria-hidden="true"></i>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-navy-800 truncate">{file.name}</p>
            <p className="text-[11px] text-navy-700/70">{formatFileSize(file.size)}</p>
          </div>
          <button
            type="button"
            onClick={clearFile}
            aria-label={t('views.registration.document.remove_aria', 'Remover documento')}
            className="shrink-0 w-[32px] h-[32px] rounded-full border-0 bg-transparent cursor-pointer text-mist-500 hover:text-alert-caution hover:bg-alert-caution/10 transition-colors"
          >
            <i className="fa fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_DOC_TYPES.join(',')}
        className="hidden"
        onChange={(e) => selectFile(e.target.files?.[0])}
      />

      <StepActions
        submitLabel={t('views.registration.document.submit', 'Enviar documento')}
        loading={submitting}
        loadingLabel={t('views.registration.sending', 'Enviando...')}
        disabled={!file}
        onBack={onBack}
      />
    </form>
  );
}
